/**
 * @file Dropdown.js
 * @description Single select dropdown with options to add search bar and a custom option at the bottom of the list.
 *
 * @prop {boolean} allowSearch - Determines whether to show a search bar in the dropdown. Default is `false`.
 * @prop {string} defaultValue - The default selected option for the dropdown.
 * @prop {Array} options - An array of options for the dropdown.
 * @prop {function} onChange - A callback function that is called when an option is selected.
 * @prop {boolean} disabled - Determines whether the dropdown is disabled. Default is `false`.
 * @prop {boolean} isBottomOptionEnabled - Determines whether to show an additional custom bottom option in the dropdown. Default is `false`.
 * @prop {string} bottomOptionLabel - The label for the custom bottom option.
 * @prop {function} onBottomOptionClick - A callback function that is called when the custom bottom option is clicked.
 * @prop {number} tabIndex - The tab order for the dropdown element. Default is `0`.
 * @prop {string} maxOpenHeight - Maximum height of option list when Dropdown is open, in px. Default is '300px'.
 * @prop {boolean} overlappingOptions - Indicating that the option list overlays the content beneath with absolute position, rather than being a relatively positioned element occupying space in the parent container.
 */

import { useState, useEffect } from 'react';
import { useWindowListener } from '../helpers/useWindowListener';
import '../helpers/Dropdown.css';
import { v4 as uuidv4 } from 'uuid';
import { getText } from '../../i18n';

export function Dropdown({
    allowSearch = false,
    defaultValue,
    options,
    onChange,
    disabled = false,
    isTitleEnabled = false,
    titleLabel = '',
    displayStarIcon = false,
    height = '100%',
    width = '100%',
    isBottomOptionEnabled = false,
    bottomOptionLabel = '',
    onBottomOptionClick,
    displayRelative = true,
    tabIndex = 0,
    useOnChangeValueFallback = false,
    maxOpenHeight = '300px',
    overlappingOptions = true,
}) {
    const [showOptionList, setShowOptionList] = useState(false);
    const [selectedText, setSelectedText] = useState(defaultValue);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const toggleShowOptionList = event => {
        event.preventDefault();
        if (!disabled) setShowOptionList(!showOptionList);
        setFilteredOptions(options);
    };

    useEffect(() => {
        setFilteredOptions(options);
    }, [options]);

    useWindowListener('mousedown', event => {
        if (
            !event.target.classList.contains('selectedText') &&
            !event.target.classList.contains('selectList') &&
            !event.target.classList.contains('selectOption') &&
            !event.target.classList.contains('bi') &&
            !event.target.classList.contains('searchBar') &&
            !event.target.classList.contains('bottomOption')
        ) {
            setShowOptionList(false);
            setFilteredOptions(options);
        }
    });

    const handleOptionSelect = option => {
        setSelectedText(option.label);
        setShowOptionList(false);
        setFilteredOptions(options);
        if (useOnChangeValueFallback) onChange(option.value || option.id);
        else onChange(option.value);
    };

    useEffect(() => {
        if (!defaultValue) {
            const newValue = options[0]?.label ?? '';
            setSelectedText(newValue);
            onChange(newValue);
        } else {
            setSelectedText(defaultValue);
        }
    }, [options, defaultValue]);

    const handleCharacterTyped = event => {
        var newOptions = options.filter(option =>
            option.label
                .toLowerCase()
                .includes(event.target.value.toLowerCase()),
        );
        setFilteredOptions(newOptions);
    };
    return (
        <div
            className={displayRelative && 'relative-position'}
            style={{ height }}
        >
            <div
                className={
                    'dropdownBox selectedText' +
                    (showOptionList ? ' active' : '') +
                    (disabled ? ' disabled' : '')
                }
                style={{ height: '100%' }}
                onClick={event => toggleShowOptionList(event)}
                tabIndex={tabIndex}
                role="combobox"
                aria-expanded={showOptionList}
                aria-haspopup="listbox"
            >
                <div className="dropdownLabel">
                    {disabled ? defaultValue : selectedText}
                </div>
                <i
                    className={
                        'bi bi-chevron-' + (showOptionList ? 'up' : 'down')
                    }
                />
            </div>

            {showOptionList && (
                <ul
                    className={
                        'selectList' +
                        (displayRelative ? '' : ' relative-position')
                    }
                    style={{
                        maxHeight: maxOpenHeight,
                        position: overlappingOptions ? 'absolute' : 'relative',
                    }}
                >
                    {isTitleEnabled && (
                        <>
                            <div className="dropdown-title-container">
                                {displayStarIcon && (
                                    <svg
                                        width="17"
                                        height="16"
                                        viewBox="0 0 17 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M3.78642 14.8508C3.70862 15.2942 4.14619 15.6413 4.53263 15.4427L8.92185 13.1868L13.3111 15.4427C13.6975 15.6413 14.1351 15.2942 14.0573 14.8508L13.2276 10.1213L16.7499 6.76462C17.0791 6.45092 16.9087 5.87737 16.4677 5.81472L11.5692 5.11885L9.38508 0.792305C9.18834 0.402565 8.65537 0.402565 8.45862 0.792305L6.2745 5.11885L1.37602 5.81472C0.935035 5.87737 0.764635 6.45092 1.09382 6.76462L4.61615 10.1213L3.78642 14.8508ZM8.69102 12.0826L5.00527 13.977L5.69953 10.0197C5.73225 9.83316 5.67072 9.64214 5.53678 9.5145L2.62994 6.7444L6.68225 6.16873C6.84976 6.14494 6.99603 6.03795 7.07506 5.88141L8.92185 2.22308L10.7686 5.88141C10.8477 6.03795 10.9939 6.14494 11.1615 6.16873L15.2138 6.7444L12.3069 9.5145C12.173 9.64214 12.1115 9.83316 12.1442 10.0197L12.8384 13.977L9.15268 12.0826C9.00709 12.0078 8.83662 12.0078 8.69102 12.0826Z"
                                            fill="black"
                                        />
                                    </svg>
                                )}
                                <p
                                    className={`dropdownTitle ${displayStarIcon ? 'leftAlign' : ''}`}
                                >
                                    {titleLabel}
                                </p>
                            </div>
                            <hr className="dropdownDivider my-2" />
                        </>
                    )}
                    {allowSearch && (
                        <>
                            <div className="dropdownSearchBar">
                                <i className="bi bi-search dropdownSearchIcon" />
                                <input
                                    className="searchBar"
                                    onChange={handleCharacterTyped}
                                    type="text"
                                    placeholder={getText('msg_bce06414177f')}
                                />
                            </div>
                        </>
                    )}
                    <div className="mb-2">
                        {filteredOptions.map(option => (
                            <li
                                className="selectOption"
                                key={uuidv4()}
                                onClick={() => handleOptionSelect(option)}
                                style={{
                                    ...(option.size && {
                                        fontSize: option.size,
                                    }),
                                    ...(option.bold && { fontWeight: 'bold' }),
                                    ...(option.color?.hex && {
                                        color: option.color.hex,
                                    }),
                                }}
                            >
                                {option.label}
                            </li>
                        ))}
                    </div>

                    {isBottomOptionEnabled && (
                        <>
                            <p
                                className="bottomOption"
                                onClick={() => {
                                    onBottomOptionClick();
                                    setShowOptionList(false);
                                }}
                            >
                                {bottomOptionLabel}
                            </p>
                        </>
                    )}
                </ul>
            )}
        </div>
    );
}
